/**
 * Admin inbox — `/api/admin/v2/inbox`.
 *
 * One list of conversations across WhatsApp, SMS and email. SMS and email
 * threads are read and answered here; a WhatsApp row is opened in the WhatsApp
 * inbox, which owns templates, media and the 24-hour rule.
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';

export type InboxChannel = 'whatsapp' | 'sms' | 'email';

export interface InboxConversation {
  channel: InboxChannel;
  /** Phone digits for WhatsApp and SMS; the address for email. */
  peer: string;
  name?: string | null;
  last_message_at?: number | null;
  last_message_preview?: string | null;
  unread_count: number;
  /** WhatsApp only: which of our numbers the conversation is on. */
  number_id?: string | null;
  /** Email only: the mailbox the conversation was last on. */
  mailbox?: string | null;
  /** SMS only: the line (smsLines.ts) the conversation was last on. */
  line?: string | null;
}

/** An address email is sent from and replies arrive on. */
export interface Mailbox {
  email: string;
  name: string;
  /** Signature HTML added to mail sent from it; {{name}} is the sender's name. */
  signature_html?: string;
}

export interface InboxMessage {
  direction: 'inbound' | 'outbound';
  body: string;
  subject?: string | null;
  /** Email: which of our addresses it was sent to or from; SMS: which line. */
  our_address?: string | null;
  at: number;
}

type Envelope<T> = { status: number; data: T };
const BASE = '/api/admin/v2/inbox';

export const inboxKeys = {
  list: (params: unknown = {}) => ['inbox', 'list', params] as const,
  thread: (channel: string, peer: string) => ['inbox', 'thread', channel, peer] as const,
};

export const useInboxConversationsQuery = (params: { channel?: string; search?: string; mailbox?: string; line?: string } = {}) => {
  const queryFn = useGetQueryFn<Envelope<{ conversations: InboxConversation[]; has_more: boolean }>>({
    defaultErrorMessage: 'Could not load the inbox',
    endpoint: `${BASE}/conversations`,
  });
  return useQuery({
    queryKey: inboxKeys.list(params),
    queryFn: () => queryFn({ ...params }),
    placeholderData: keepPreviousData,
    // New replies should surface without a manual refresh, including after the
    // tab has been in the background (the app turns focus refetch off globally).
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    select: (response) => response?.data,
  });
};

export const useInboxThreadQuery = (channel: InboxChannel | null, peer: string | null) => {
  const queryFn = useGetQueryFn<Envelope<{ messages: InboxMessage[] }>>({
    defaultErrorMessage: 'Could not load the conversation',
    endpoint: `${BASE}/conversations/${channel}/${encodeURIComponent(peer || '')}`,
  });
  return useQuery({
    queryKey: inboxKeys.thread(channel || '', peer || ''),
    queryFn: () => queryFn(),
    enabled: Boolean(channel && peer && channel !== 'whatsapp'),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    select: (response) => response?.data,
  });
};

type Target = { channel: InboxChannel; peer: string };

export const useInboxReplyMutation = () => {
  const mutationFn = useGetMutationFn<Target & { body: string; subject?: string; html?: string; from?: string; signature?: boolean }, Envelope<{ success: boolean }>>({
    defaultErrorMessage: 'The reply was not sent',
    endpoint: ({ channel, peer }) => `${BASE}/conversations/${channel}/${encodeURIComponent(peer)}/reply`,
    method: 'POST',
  });
  return useMutation({ mutationFn });
};

export const useMailboxesQuery = () => {
  const queryFn = useGetQueryFn<Envelope<{ mailboxes: Mailbox[] }>>({
    defaultErrorMessage: 'Could not load the mailboxes',
    endpoint: '/api/admin/v2/messaging/mailboxes',
  });
  return useQuery({
    queryKey: ['inbox', 'mailboxes'],
    queryFn: () => queryFn(),
    staleTime: Infinity,
    select: (response) => response?.data?.mailboxes ?? [],
  });
};

export const useMailboxSignatureMutation = () => {
  const mutationFn = useGetMutationFn<{ email: string; signature_html: string }, Envelope<{ success: boolean }>>({
    defaultErrorMessage: 'The signature was not saved',
    endpoint: ({ email }) => `/api/admin/v2/messaging/mailboxes/${encodeURIComponent(email)}/signature`,
    method: 'PUT',
  });
  return useMutation({ mutationFn });
};

export const useInboxReadMutation = () => {
  const mutationFn = useGetMutationFn<Target, Envelope<{ success: boolean }>>({
    defaultErrorMessage: 'Could not mark the conversation read',
    endpoint: ({ channel, peer }) => `${BASE}/conversations/${channel}/${encodeURIComponent(peer)}/read`,
    method: 'POST',
  });
  return useMutation({ mutationFn });
};
